import React, {useCallback} from 'react';

import {observer} from 'mobx-react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

import ConditionalComponent from '../../Shared/ConditionalComponent';
import InvitedMembersRow from '../SettingsShared/InvitedMembersRow';
import StyledOptionSelect from '../SettingsShared/StyledOptionSelect';
import usePrivacySection from '../SettingsShared/usePrivacySection';
import {projectPrivacyLevelEnum} from '../../../serverClientShared/sharedEnums';

const labelClass = 'block mb-1 text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const subLabelClass = 'text-xs text-main-hover';

const LEVELS = [
    {value: projectPrivacyLevelEnum.PRIVATE, title: 'Private', body: 'Only invited members have access', Icon: LockOutlinedIcon, iconClass: 'text-caution-warning'},
    {value: projectPrivacyLevelEnum.WORKSPACE_PUBLIC, title: 'Workspace public', body: 'All workspace members have access', Icon: VisibilityOutlinedIcon, iconClass: 'text-classic-button-normal'},
    {value: projectPrivacyLevelEnum.PUBLIC, title: 'Public', body: 'All workspace members and guests have access', Icon: PublicOutlinedIcon, iconClass: 'text-classic-button-normal'},
];

const LevelBanner = ({level}) => {
    const meta = LEVELS.find((l) => l.value === level);
    if (!meta) return null;
    const bgClass = level === projectPrivacyLevelEnum.PRIVATE
        ? 'bg-[color-mix(in_srgb,var(--caution-warning-color)_10%,transparent)] border-[color-mix(in_srgb,var(--caution-warning-color)_30%,transparent)]'
        : 'bg-[color-mix(in_srgb,var(--classicbutton-normal-color)_8%,transparent)] border-[color-mix(in_srgb,var(--classicbutton-normal-color)_30%,transparent)]';
    return (
        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-input border ${bgClass}`}>
            <meta.Icon className={meta.iconClass} style={{fontSize: 20}} />
            <div>
                <div className="text-sm font-medium text-main-normal">{meta.title}</div>
                <div className="text-xs text-main-hover">{meta.body}</div>
            </div>
        </div>
    );
};

const ProjectPrivacySection = observer(({settings}) => {
    const {addRef, handleOpenShare, isAccessLoaded} = usePrivacySection(settings, {isProjectLevel: true});

    const handleRemoveInvited = useCallback((id) => {
        settings.setEditedAccessSources(settings.editedAccessSources.filter((s) => s !== id));
        settings.setAccessGroups(settings.accessGroups.filter((g) => g.GroupId !== id));
        settings.setAccessMembers(settings.accessMembers.filter((m) => m._id !== id));
    }, [settings]);

    const isPrivate = settings.editedPrivacyLevel === projectPrivacyLevelEnum.PRIVATE;
    const availableLevels = settings.isMirrored
        ? LEVELS.filter((l) => l.value !== projectPrivacyLevelEnum.PUBLIC)
        : LEVELS;

    return (
        <div>
            <div className="flex items-start justify-between mb-2 gap-3">
                <div>
                    <span className={labelClass}>PRIVACY</span>
                    <p className={subLabelClass}>Set an access level to this project</p>
                </div>
                <StyledOptionSelect
                    value={settings.editedPrivacyLevel}
                    onChange={settings.updatePrivacyLevel}
                    options={availableLevels}
                    data-testid="project-privacy-select"
                />
            </div>
            <LevelBanner level={settings.editedPrivacyLevel} />
            <ConditionalComponent if={isPrivate}>
                <InvitedMembersRow groups={settings.accessGroups} members={settings.accessMembers} addRef={addRef} onAdd={handleOpenShare} onRemove={handleRemoveInvited} isAccessLoaded={isAccessLoaded} />
            </ConditionalComponent>
        </div>
    );
});

export default ProjectPrivacySection;
